/**
 * GridMind — Riscos Psicossociais (NR-1 / PROART).
 * GridProfile / NeuroSynx Ltda
 *
 * Página principal (admin/gestor). Abas:
 *   Campanhas · Dashboard · Planos de Ação · Relatórios · Evidências · Indicadores
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  Brain, Plus, Trash2, Loader2, Link2, QrCode, BarChart3, ClipboardList,
  FileText, ShieldCheck, Sparkles, RefreshCw, Send, Copy, CheckCircle2,
  AlertTriangle, FileSpreadsheet, Paperclip, Activity, Lock, Mail, MessageCircle,
} from 'lucide-react';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LineChart, Line, CartesianGrid,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const NIVEL_COR = {
  baixo: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  atencao: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  alto: 'bg-orange-100 text-orange-700 border-orange-200',
  critico: 'bg-red-100 text-red-700 border-red-200',
};
const NIVEL_LABEL = { baixo: 'Baixo', atencao: 'Atenção', alto: 'Alto', critico: 'Crítico' };
const NIVEL_HEX = { baixo: '#16a34a', atencao: '#eab308', alto: '#f97316', critico: '#dc2626' };
const hexNivel = (n) => NIVEL_HEX[n] || '#94a3b8';
const STATUS_LABEL = { em_elaboracao: 'Em elaboração', publicada: 'Publicada', encerrada: 'Encerrada', arquivada: 'Arquivada' };
const STATUS_COR = {
  em_elaboracao: 'bg-slate-100 text-slate-600',
  publicada: 'bg-blue-100 text-blue-700',
  encerrada: 'bg-purple-100 text-purple-700',
  arquivada: 'bg-slate-100 text-slate-400',
};

const NivelBadge = ({ nivel }) =>
  nivel ? <Badge className={`border ${NIVEL_COR[nivel] || ''}`}>{NIVEL_LABEL[nivel] || nivel}</Badge>
        : <Badge className="bg-slate-100 text-slate-400">sem dados</Badge>;

const fmt = (m) => (m === null || m === undefined) ? 'sem dados' : Number(m).toFixed(2);

// =========================================================================
// Página principal
// =========================================================================
const GridMindPage = () => {
  const location = useLocation();
  const [campanhas, setCampanhas] = useState([]);
  const [selecionada, setSelecionada] = useState(null);
  const [loading, setLoading] = useState(true);

  const TAB_MAP = {
    gridmind_campanhas: 'campanhas',
    gridmind_dashboard: 'dashboard',
    gridmind_planos: 'planos',
    gridmind_relatorios: 'relatorios',
    gridmind_evidencias: 'evidencias',
    gridmind_indicadores: 'indicadores',
  };

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && TAB_MAP[tabFromUrl]) return TAB_MAP[tabFromUrl];
    return 'campanhas';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && TAB_MAP[tabFromUrl]) {
      setActiveTab(TAB_MAP[tabFromUrl]);
    }
  }, [location.search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/gridmind/campanhas`, auth());
      setCampanhas(r.data || []);
      if (r.data?.length && !selecionada) setSelecionada(r.data[0]);
    } catch (e) {
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  }, [selecionada]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-indigo-600 rounded-xl"><Brain className="w-7 h-7 text-white" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">GridMind — Riscos Psicossociais</h1>
          <p className="text-sm text-slate-500">Gestão de riscos psicossociais (NR-1) · Metodologia PROART</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="campanhas"><ClipboardList className="w-4 h-4 mr-1" /> Campanhas</TabsTrigger>
          <TabsTrigger value="dashboard"><BarChart3 className="w-4 h-4 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="planos"><CheckCircle2 className="w-4 h-4 mr-1" /> Planos de Ação</TabsTrigger>
          <TabsTrigger value="relatorios"><FileText className="w-4 h-4 mr-1" /> Relatórios</TabsTrigger>
          <TabsTrigger value="evidencias"><Paperclip className="w-4 h-4 mr-1" /> Evidências</TabsTrigger>
          <TabsTrigger value="indicadores"><Activity className="w-4 h-4 mr-1" /> Indicadores</TabsTrigger>
        </TabsList>

        <TabsContent value="campanhas">
          <CampanhasTab campanhas={campanhas} loading={loading} reload={load}
                        selecionada={selecionada} setSelecionada={setSelecionada} />
        </TabsContent>
        <TabsContent value="dashboard">
          <DashboardTab campanha={selecionada} campanhas={campanhas} setSelecionada={setSelecionada} />
        </TabsContent>
        <TabsContent value="planos">
          <PlanosTab campanha={selecionada} campanhas={campanhas} setSelecionada={setSelecionada} />
        </TabsContent>
        <TabsContent value="relatorios">
          <RelatoriosTab campanha={selecionada} campanhas={campanhas} setSelecionada={setSelecionada} />
        </TabsContent>
        <TabsContent value="evidencias">
          <EvidenciasTab campanha={selecionada} campanhas={campanhas} setSelecionada={setSelecionada} />
        </TabsContent>
        <TabsContent value="indicadores">
          <IndicadoresTab campanha={selecionada} campanhas={campanhas} setSelecionada={setSelecionada} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Seletor de campanha reutilizável
const SeletorCampanha = ({ campanhas, campanha, setSelecionada }) => (
  <div className="mb-4 max-w-md">
    <Label className="text-xs text-slate-500">Campanha</Label>
    <Select value={campanha?.id || ''} onValueChange={(id) => setSelecionada(campanhas.find(c => c.id === id))}>
      <SelectTrigger><SelectValue placeholder="Selecione uma campanha" /></SelectTrigger>
      <SelectContent>
        {campanhas.map(c => <SelectItem key={c.id} value={c.id}>{c.codigo} — {STATUS_LABEL[c.status]}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

// =========================================================================
// Aba: Campanhas
// =========================================================================
const CampanhasTab = ({ campanhas, loading, reload, selecionada, setSelecionada }) => {
  const [open, setOpen] = useState(false);
  const empty = { codigo: '', metodologia_codigo: 'PROART', descricao: '', anonima: true, min_respondentes: 5,
                  empresa_ids: [], departamento: '', cargo: '', gestor_responsavel: '', funcionario_ids: [],
                  data_inicio: '', data_fim: '',
                  perguntas_customizadas: [], perguntas_abertas_extra: [] };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);
  const [envio, setEnvio] = useState({ canal: null, destino: '', enviando: false });

  // Cadastros para os seletores de segmentação
  const [empresas, setEmpresas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcBusca, setFuncBusca] = useState('');
  const [metodologias, setMetodologias] = useState([]);

  useEffect(() => {
    (async () => {
      const safe = async (url) => { try { const r = await axios.get(`${API}${url}`, auth()); return r.data || []; } catch { return []; } };
      const [emps, deps, pos, emps2, metods] = await Promise.all([
        safe('/api/companies'), safe('/api/departments'), safe('/api/positions'), safe('/api/employees'),
        safe('/api/gridmind/metodologias'),
      ]);
      setEmpresas(emps);
      setDepartamentos(deps);
      setCargos(pos);
      setMetodologias((metods || []).filter(m => m.ativa !== false));
      // Colaboradores ativos para seleção específica de destinatários
      const ativos = (emps2 || []).filter(e => {
        const s = (e.situacao || '').trim().toLowerCase();
        const inativo = s.includes('inativo') || s.includes('demitido') || s.includes('desligado') || !!e.data_demissao;
        return !inativo;
      }).sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
      setFuncionarios(ativos);
      // Gestor = funcionário referenciado como superior direto de alguém
      const superiorIds = new Set((emps2 || []).map(e => e.superior_direto_id).filter(Boolean));
      setGestores((emps2 || []).filter(e => superiorIds.has(e.id)).sort((a, b) => (a.nome || '').localeCompare(b.nome || '')));
    })();
  }, []);

  // Ao abrir o diálogo, busca o próximo código gerado automaticamente
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/gridmind/proximo-codigo`, auth());
        setForm(f => ({ ...f, codigo: r.data?.codigo || '' }));
      } catch { /* mantém vazio; backend gera no salvamento */ }
    })();
  }, [open]);

  const toggleEmpresa = (id) => setForm(f => {
    const sel = new Set(f.empresa_ids || []);
    sel.has(id) ? sel.delete(id) : sel.add(id);
    return { ...f, empresa_ids: Array.from(sel) };
  });

  const toggleFuncionario = (id) => setForm(f => {
    const sel = new Set(f.funcionario_ids || []);
    sel.has(id) ? sel.delete(id) : sel.add(id);
    return { ...f, funcionario_ids: Array.from(sel) };
  });
  const funcionariosFiltrados = (funcionarios || []).filter(e => {
    const q = funcBusca.trim().toLowerCase();
    if (!q) return true;
    return [(e.nome || ''), (e.cargo || ''), (e.departamento || '')].join(' ').toLowerCase().includes(q);
  });

  const addPerguntaCustom = () => setForm(f => ({
    ...f,
    perguntas_customizadas: [...(f.perguntas_customizadas || []),
      { id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, texto: '', tipo: 'likert', invertida: false, obrigatoria: true }],
  }));
  const updPerguntaCustom = (i, patch) => setForm(f => {
    const arr = [...(f.perguntas_customizadas || [])]; arr[i] = { ...arr[i], ...patch }; return { ...f, perguntas_customizadas: arr };
  });
  const delPerguntaCustom = (i) => setForm(f => ({ ...f, perguntas_customizadas: (f.perguntas_customizadas || []).filter((_, k) => k !== i) }));
  const addAberta = () => setForm(f => ({ ...f, perguntas_abertas_extra: [...(f.perguntas_abertas_extra || []), ''] }));
  const updAberta = (i, val) => setForm(f => { const arr = [...(f.perguntas_abertas_extra || [])]; arr[i] = val; return { ...f, perguntas_abertas_extra: arr }; });
  const delAberta = (i) => setForm(f => ({ ...f, perguntas_abertas_extra: (f.perguntas_abertas_extra || []).filter((_, k) => k !== i) }));

  const criar = async () => {
    const customInvalida = (form.perguntas_customizadas || []).some(p => !(p.texto || '').trim());
    if (customInvalida) { toast.error('Preencha o texto de todas as perguntas customizadas (ou remova-as)'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        metodologia_codigo: form.metodologia_codigo || 'PROART',
        perguntas_abertas_extra: (form.perguntas_abertas_extra || []).map(t => (t || '').trim()).filter(Boolean),
        perguntas_customizadas: (form.perguntas_customizadas || []).filter(p => (p.texto || '').trim()),
      };
      await axios.post(`${API}/api/gridmind/campanhas`, payload, auth());
      toast.success('Campanha criada');
      setOpen(false); setForm(empty); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar');
    } finally { setSaving(false); }
  };

  const acao = async (c, endpoint, msg) => {
    try {
      await axios.post(`${API}/api/gridmind/campanhas/${c.id}/${endpoint}`, {}, auth());
      toast.success(msg); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const excluir = async (c) => {
    if (!window.confirm(`Excluir campanha ${c.codigo}?`)) return;
    try { await axios.delete(`${API}/api/gridmind/campanhas/${c.id}`, auth()); toast.success('Excluída'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const verLink = async (c) => {
    try {
      const origin = window.location.origin;
      const r = await axios.get(`${API}/api/gridmind/campanhas/${c.id}/link?origin=${encodeURIComponent(origin)}`, auth());
      const url = r.data.full_url || (origin + r.data.url_relativa);
      setLinkInfo({ ...r.data, full: url, codigo: c.codigo, id: c.id });
      setEnvio({ canal: null, destino: '', enviando: false });
    } catch { toast.error('Erro ao gerar link'); }
  };

  const copiar = (txt) => { navigator.clipboard.writeText(txt); toast.success('Link copiado'); };

  const enviarLink = async () => {
    if (!envio.canal || !envio.destino.trim()) return;
    setEnvio(e => ({ ...e, enviando: true }));
    try {
      await axios.post(`${API}/api/gridmind/campanhas/${linkInfo.id}/enviar-link`, {
        canal: envio.canal,
        destino: envio.destino.trim(),
        base_url: window.location.origin,
      }, auth());
      toast.success(envio.canal === 'email' ? 'E-mail enviado!' : 'WhatsApp enviado!');
      setEnvio({ canal: null, destino: '', enviando: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar');
      setEnvio(e2 => ({ ...e2, enviando: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{campanhas.length} campanha(s)</p>
        <Button onClick={() => setOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-1" /> Nova Campanha
        </Button>
      </div>

      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" /></div>
        : campanhas.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-400">Nenhuma campanha. Crie a primeira.</CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {campanhas.map(c => (
              <Card key={c.id} className={selecionada?.id === c.id ? 'border-indigo-300' : ''}>
                <CardContent className="py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="cursor-pointer" onClick={() => setSelecionada(c)}>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{c.codigo}</span>
                        <Badge className={STATUS_COR[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                        {c.anonima
                          ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200"><Lock className="w-3 h-3 mr-1" />Anônima</Badge>
                          : <Badge className="bg-blue-50 text-blue-700 border border-blue-200">Identificada</Badge>}
                      </div>
                      <p className="text-sm text-slate-500">{c.descricao || '—'}</p>
                      <p className="text-xs text-slate-400 mt-1">{c.total_respostas || 0} resposta(s) · mín. {c.min_respondentes} p/ recorte</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.status === 'em_elaboracao' && (
                        <Button size="sm" variant="outline" onClick={() => acao(c, 'publicar', 'Publicada')}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Publicar
                        </Button>
                      )}
                      {c.status === 'publicada' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => verLink(c)}><Link2 className="w-3.5 h-3.5 mr-1" /> Link/QR</Button>
                          <Button size="sm" variant="outline" onClick={() => acao(c, 'encerrar', 'Encerrada')}>Encerrar</Button>
                        </>
                      )}
                      {(c.status === 'publicada' || c.status === 'encerrada') && (
                        <Button size="sm" variant="outline" onClick={() => acao(c, 'arquivar', 'Arquivada')}>Arquivar</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => excluir(c)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {/* Dialog: nova campanha */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Campanha{(() => { const m = metodologias.find(x => x.codigo === form.metodologia_codigo); return m ? ` — ${m.codigo}` : ''; })()}</DialogTitle>
            <DialogDescription>Defina a metodologia, parâmetros, segmentação e perguntas adicionais. O anonimato é configurável por campanha.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Código</Label>
              <Input value={form.codigo} readOnly disabled className="bg-slate-50 text-slate-600" placeholder="Gerado automaticamente" />
              <p className="text-xs text-slate-400 mt-1">Gerado automaticamente no formato PSICO-{new Date().getFullYear()}-0000.</p>
            </div>
            <div>
              <Label>Metodologia</Label>
              <Select value={form.metodologia_codigo}
                      onValueChange={v => setForm({ ...form, metodologia_codigo: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione a metodologia" /></SelectTrigger>
                <SelectContent>
                  {metodologias.map(m => <SelectItem key={m.codigo} value={m.codigo}>{m.nome || m.codigo}</SelectItem>)}
                </SelectContent>
              </Select>
              {(() => {
                const m = metodologias.find(x => x.codigo === form.metodologia_codigo);
                return m?.descricao ? <p className="text-xs text-slate-400 mt-1">{m.descricao}</p> : null;
              })()}
            </div>
            <div><Label>Descrição</Label><Textarea value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} rows={2} /></div>

            <div className="rounded-md border p-3 space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Segmentação / abrangência</p>

              {/* Empresas — seleção múltipla (vazio = todas) */}
              <div>
                <Label>Empresas</Label>
                <div className="mt-1 rounded-md border max-h-36 overflow-y-auto divide-y">
                  <label className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={(form.empresa_ids || []).length === 0}
                              onCheckedChange={() => setForm(f => ({ ...f, empresa_ids: [] }))} />
                    <span className="font-medium">Todas as Empresas</span>
                  </label>
                  {empresas.map(emp => (
                    <label key={emp.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-slate-50">
                      <Checkbox checked={(form.empresa_ids || []).includes(emp.id)}
                                onCheckedChange={() => toggleEmpresa(emp.id)} />
                      <span>{emp.name || emp.razao_social || emp.id}</span>
                    </label>
                  ))}
                  {empresas.length === 0 && <p className="p-2 text-xs text-slate-400">Nenhuma empresa cadastrada.</p>}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {(form.empresa_ids || []).length === 0 ? 'Vale para todas as empresas.' : `${form.empresa_ids.length} empresa(s) selecionada(s).`}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Departamento</Label>
                  <Select value={form.departamento || '__todos__'}
                          onValueChange={v => setForm({ ...form, departamento: v === '__todos__' ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__todos__">Todos os Departamentos</SelectItem>
                      {departamentos.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cargo</Label>
                  <Select value={form.cargo || '__todos__'}
                          onValueChange={v => setForm({ ...form, cargo: v === '__todos__' ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__todos__">Todos os Cargos</SelectItem>
                      {cargos.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Gestor responsável</Label>
                  <Select value={form.gestor_responsavel || '__todos__'}
                          onValueChange={v => setForm({ ...form, gestor_responsavel: v === '__todos__' ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__todos__">Todos os Gestores</SelectItem>
                      {gestores.map(g => <SelectItem key={g.id} value={g.nome}>{g.nome}{g.cargo ? ` — ${g.cargo}` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Início</Label><Input type="date" value={form.data_inicio} onChange={e => setForm({ ...form, data_inicio: e.target.value })} /></div>
                <div><Label>Fim</Label><Input type="date" value={form.data_fim} onChange={e => setForm({ ...form, data_fim: e.target.value })} /></div>
              </div>

              {/* Colaboradores específicos — seleção múltipla (vazio = não restringe por colaborador) */}
              <div>
                <Label>Colaboradores específicos (opcional)</Label>
                <Input className="mt-1" placeholder="Buscar por nome, cargo ou departamento…"
                       value={funcBusca} onChange={e => setFuncBusca(e.target.value)} />
                <div className="mt-1 rounded-md border max-h-44 overflow-y-auto divide-y">
                  {funcionariosFiltrados.map(f => (
                    <label key={f.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-slate-50">
                      <Checkbox checked={(form.funcionario_ids || []).includes(f.id)}
                                onCheckedChange={() => toggleFuncionario(f.id)} />
                      <span className="flex-1">
                        {f.nome || f.id}
                        {(f.cargo || f.departamento) && (
                          <span className="text-slate-400"> — {[f.cargo, f.departamento].filter(Boolean).join(' · ')}</span>
                        )}
                      </span>
                    </label>
                  ))}
                  {funcionarios.length === 0 && <p className="p-2 text-xs text-slate-400">Nenhum colaborador cadastrado.</p>}
                  {funcionarios.length > 0 && funcionariosFiltrados.length === 0 && (
                    <p className="p-2 text-xs text-slate-400">Nenhum colaborador encontrado para a busca.</p>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-400">
                    {(form.funcionario_ids || []).length === 0
                      ? 'Sem seleção: a pesquisa não é restringida a colaboradores específicos.'
                      : `${form.funcionario_ids.length} colaborador(es) selecionado(s) — apenas eles receberão a pesquisa no portal.`}
                  </p>
                  {(form.funcionario_ids || []).length > 0 && (
                    <button type="button" className="text-xs text-indigo-600 hover:underline"
                            onClick={() => setForm(f => ({ ...f, funcionario_ids: [] }))}>
                      Limpar seleção
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="font-medium">Campanha anônima</Label>
                <p className="text-xs text-slate-500">Não registra a identidade do respondente — apenas o segmento.</p>
              </div>
              <Switch checked={form.anonima} onCheckedChange={v => setForm({ ...form, anonima: v })} />
            </div>
            <div>
              <Label>Mínimo de respondentes por recorte (anonimato)</Label>
              <Input type="number" min={1} value={form.min_respondentes}
                     onChange={e => setForm({ ...form, min_respondentes: Number(e.target.value) })} />
              <p className="text-xs text-slate-400 mt-1">Recortes com menos respondentes que este valor são ocultados no dashboard.</p>
            </div>

            {/* Perguntas customizadas (complementares ao instrumento PROART) */}
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Perguntas customizadas</Label>
                  <p className="text-xs text-slate-500">Itens adicionais ao instrumento. "Invertida" inverte a escala no cálculo de risco.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addPerguntaCustom}><Plus className="w-3 h-3 mr-1" /> Adicionar</Button>
              </div>
              {(form.perguntas_customizadas || []).length === 0 && <p className="text-xs text-slate-400">Nenhuma pergunta customizada.</p>}
              {(form.perguntas_customizadas || []).map((p, i) => (
                <div key={p.id} className="rounded border p-2 space-y-2 bg-slate-50">
                  <div className="flex gap-2">
                    <Input className="flex-1" placeholder="Texto da pergunta" value={p.texto} onChange={e => updPerguntaCustom(i, { texto: e.target.value })} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => delPerguntaCustom(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={p.tipo} onValueChange={v => updPerguntaCustom(i, { tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="likert">Likert (escala)</SelectItem>
                        <SelectItem value="aberta">Aberta (texto)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-4 text-xs">
                      {p.tipo !== 'aberta' && (
                        <label className="flex items-center gap-1"><Switch checked={!!p.invertida} onCheckedChange={v => updPerguntaCustom(i, { invertida: v })} /> Invertida</label>
                      )}
                      <label className="flex items-center gap-1"><Switch checked={p.obrigatoria !== false} onCheckedChange={v => updPerguntaCustom(i, { obrigatoria: v })} /> Obrigatória</label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Perguntas abertas extras */}
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Perguntas abertas extras</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAberta}><Plus className="w-3 h-3 mr-1" /> Adicionar</Button>
              </div>
              {(form.perguntas_abertas_extra || []).length === 0 && <p className="text-xs text-slate-400">Nenhuma pergunta aberta extra.</p>}
              {(form.perguntas_abertas_extra || []).map((t, i) => (
                <div key={i} className="flex gap-2">
                  <Input className="flex-1" placeholder="Ex.: Sugestões para melhorar o ambiente" value={t} onChange={e => updAberta(i, e.target.value)} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => delAberta(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={criar} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: link/QR */}
      <Dialog open={!!linkInfo} onOpenChange={() => setLinkInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Link de resposta — {linkInfo?.codigo}</DialogTitle></DialogHeader>
          {linkInfo && (
            <div className="space-y-4 text-center">
              {linkInfo.qr_code && <img src={linkInfo.qr_code} alt="QR Code" className="w-48 h-48 mx-auto border rounded-lg" />}
              <div className="flex items-center gap-2">
                <Input readOnly value={linkInfo.full} className="text-xs" />
                <Button size="sm" variant="outline" onClick={() => copiar(linkInfo.full)}><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-slate-500">{linkInfo.anonima ? 'Respostas anônimas.' : 'Respostas identificadas.'}</p>

              <div className="pt-3 border-t text-left space-y-3">
                {!envio.canal ? (
                  <>
                    <p className="text-sm font-medium text-slate-700 text-center">Enviar link por:</p>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => setEnvio({ canal: 'email', destino: '', enviando: false })}>
                        <Mail className="w-4 h-4 mr-2" /> E-mail
                      </Button>
                      <Button variant="outline" className="flex-1" onClick={() => setEnvio({ canal: 'whatsapp', destino: '', enviando: false })}>
                        <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Label className="text-sm">
                      {envio.canal === 'email' ? 'E-mail de destino' : 'WhatsApp de destino (com DDD)'}
                    </Label>
                    <Input
                      autoFocus
                      type={envio.canal === 'email' ? 'email' : 'tel'}
                      placeholder={envio.canal === 'email' ? 'nome@empresa.com' : '+55 11 99999-9999'}
                      value={envio.destino}
                      onChange={e => setEnvio(s => ({ ...s, destino: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') enviarLink(); }}
                    />
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEnvio({ canal: null, destino: '', enviando: false })} disabled={envio.enviando}>
                        Voltar
                      </Button>
                      <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={enviarLink} disabled={envio.enviando || !envio.destino.trim()}>
                        {envio.enviando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-2" />}
                        Enviar
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =========================================================================
// Aba: Dashboard
// =========================================================================
const DashboardTab = ({ campanha, campanhas, setSelecionada }) => {
  const [recorte, setRecorte] = useState('geral');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const load = useCallback(async () => {
    if (!campanha) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/gridmind/campanhas/${campanha.id}/dashboard?recorte=${recorte}`, auth());
      setData(r.data);
    } catch { toast.error('Erro ao carregar dashboard'); } finally { setLoading(false); }
  }, [campanha, recorte]);

  useEffect(() => { load(); }, [load]);

  const recalcular = async () => {
    try { await axios.post(`${API}/api/gridmind/campanhas/${campanha.id}/calcular`, {}, auth()); toast.success('Recalculado'); load(); }
    catch { toast.error('Erro'); }
  };

  const diagnosticar = async () => {
    setDiagLoading(true); setDiag(null);
    try {
      const r = await axios.post(`${API}/api/gridmind/campanhas/${campanha.id}/diagnostico-ia`, {}, auth());
      setDiag(r.data.diagnostico);
    } catch (e) { toast.error(e.response?.data?.detail || 'IA indisponível'); } finally { setDiagLoading(false); }
  };

  if (!campanha) return <SeletorVazio campanhas={campanhas} setSelecionada={setSelecionada} />;
  const geral = data?.geral;

  return (
    <div className="space-y-4">
      <SeletorCampanha campanhas={campanhas} campanha={campanha} setSelecionada={setSelecionada} />
      <div className="flex flex-wrap gap-2 items-end justify-between">
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs text-slate-500">Recorte</Label>
            <Select value={recorte} onValueChange={setRecorte}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="geral">Geral</SelectItem>
                <SelectItem value="departamento">Por Departamento</SelectItem>
                <SelectItem value="unidade">Por Unidade</SelectItem>
                <SelectItem value="cargo">Por Cargo</SelectItem>
                <SelectItem value="gestor">Por Gestor/Área</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={recalcular}><RefreshCw className="w-4 h-4 mr-1" /> Recalcular</Button>
        </div>
        <Button variant="outline" size="sm" onClick={diagnosticar} disabled={diagLoading}>
          {diagLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />} Diagnóstico IA (Maya)
        </Button>
      </div>

      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" /></div> : (
        <>
          {/* Cartões resumo */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card><CardContent className="py-4">
              <p className="text-xs text-slate-500">Média Geral</p>
              <p className="text-3xl font-bold text-slate-800">{fmt(geral?.media_geral)}</p>
            </CardContent></Card>
            <Card><CardContent className="py-4">
              <p className="text-xs text-slate-500">Nível de Risco</p>
              <div className="mt-2"><NivelBadge nivel={geral?.nivel_geral} /></div>
            </CardContent></Card>
            <Card><CardContent className="py-4">
              <p className="text-xs text-slate-500">Respondentes</p>
              <p className="text-3xl font-bold text-slate-800">{data?.total_respondentes ?? 0}</p>
            </CardContent></Card>
          </div>

          {diag && (
            <Card className="border-indigo-200 bg-indigo-50/40">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-600" /> Diagnóstico (IA)</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-slate-700 whitespace-pre-wrap">{diag}</p></CardContent>
            </Card>
          )}

          {/* Gráficos (radar / barras / tendência) */}
          {recorte === 'geral' && data?.graficos && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {(data.graficos.radar || []).some(r => r.media != null) && (
                <Card>
                  <CardHeader className="pb-1"><CardTitle className="text-sm">Perfil de risco por escala</CardTitle></CardHeader>
                  <CardContent className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={(data.graficos.radar || []).map(r => ({ ...r, media: r.media ?? 0 }))}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="eixo" tick={{ fontSize: 10 }} />
                        <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 9 }} />
                        <Radar name="Média" dataKey="media" stroke="#4f46e5" fill="#6366f1" fillOpacity={0.5} />
                        <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              {(data.graficos.barras_fatores || []).length > 0 && (
                <Card>
                  <CardHeader className="pb-1"><CardTitle className="text-sm">Fatores por nível de risco</CardTitle></CardHeader>
                  <CardContent className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(data.graficos.barras_fatores || []).slice(0, 8)} layout="vertical" margin={{ left: 10, right: 16 }}>
                        <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 9 }} />
                        <YAxis type="category" dataKey="fator" width={120} tick={{ fontSize: 9 }} />
                        <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                        <Bar dataKey="media" radius={[0, 4, 4, 0]}>
                          {(data.graficos.barras_fatores || []).slice(0, 8).map((b, i) => <Cell key={i} fill={hexNivel(b.nivel)} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              {(data.graficos.tendencia || []).length > 1 && (
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-1"><CardTitle className="text-sm">Evolução histórica (média geral)</CardTitle></CardHeader>
                  <CardContent className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={(data.graficos.tendencia || []).map(t => ({
                        data: (t.data || '').slice(0, 10), media: t.media_geral, respondentes: t.total_respondentes }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="data" tick={{ fontSize: 9 }} />
                        <YAxis domain={[0, 5]} tick={{ fontSize: 9 }} />
                        <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                        <Line type="monotone" dataKey="media" stroke="#4f46e5" strokeWidth={2} dot />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Escalas */}
          {recorte === 'geral' && geral && (
            <div className="space-y-3">
              {(geral.escalas || []).map(esc => (
                <Card key={esc.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{esc.nome}</CardTitle>
                      <div className="flex items-center gap-2"><span className="font-semibold">{fmt(esc.media)}</span><NivelBadge nivel={esc.nivel} /></div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(esc.fatores || []).map(f => (
                      <div key={f.id} className="flex items-center justify-between text-sm border-b border-slate-50 py-1">
                        <span className="text-slate-600">{f.nome}</span>
                        <div className="flex items-center gap-2"><span>{fmt(f.media)}</span><NivelBadge nivel={f.nivel} /></div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Recortes segmentados */}
          {recorte !== 'geral' && (
            <div className="grid gap-3">
              {(data?.recortes || []).map((rec, i) => (
                <Card key={i}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">{rec.segmento}</p>
                      <p className="text-xs text-slate-400">{rec.respondentes} respondente(s)</p>
                    </div>
                    {rec.suprimido ? (
                      <Badge className="bg-slate-100 text-slate-500 border"><Lock className="w-3 h-3 mr-1" /> Oculto (anonimato)</Badge>
                    ) : (
                      <div className="flex items-center gap-2"><span className="font-semibold">{fmt(rec.resultado?.media_geral)}</span><NivelBadge nivel={rec.resultado?.nivel_geral} /></div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {(data?.recortes || []).length === 0 && <p className="text-sm text-slate-400">Sem dados para este recorte.</p>}

              {/* Mapa de calor: segmento (visível) × escala */}
              {data?.heatmap && (data.heatmap.linhas || []).length > 0 && (
                <Card className="mt-2">
                  <CardHeader className="pb-1"><CardTitle className="text-sm">Mapa de calor — segmento × escala</CardTitle></CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="text-xs w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="text-left p-2 text-slate-500 font-medium">Segmento</th>
                          {(data.heatmap.escalas || []).map((e, i) => (
                            <th key={i} className="p-2 text-slate-500 font-medium text-center">{e}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(data.heatmap.linhas || []).map((linha, i) => (
                          <tr key={i}>
                            <td className="p-2 font-medium text-slate-700">{linha.segmento}</td>
                            {(linha.valores || []).map((v, j) => (
                              <td key={j} className="p-2 text-center text-white font-semibold rounded"
                                  style={{ backgroundColor: hexNivel(v.nivel) }}>
                                {fmt(v.media)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// =========================================================================
// Aba: Planos de Ação
// =========================================================================
const PlanosTab = ({ campanha, campanhas, setSelecionada }) => {
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const empty = { titulo: '', descricao: '', responsavel: '', data_limite: '', status: 'aberto' };
  const [form, setForm] = useState(empty);
  const [evidOpen, setEvidOpen] = useState(null);

  const load = useCallback(async () => {
    if (!campanha) return;
    setLoading(true);
    try { const r = await axios.get(`${API}/api/gridmind/campanhas/${campanha.id}/planos`, auth()); setPlanos(r.data || []); }
    catch { toast.error('Erro'); } finally { setLoading(false); }
  }, [campanha]);
  useEffect(() => { load(); }, [load]);

  const gerarAuto = async () => {
    try {
      const r = await axios.post(`${API}/api/gridmind/campanhas/${campanha.id}/gerar-planos`, {}, auth());
      toast.success(`${r.data.criados} plano(s) gerado(s), ${r.data.mantidos} mantido(s)`); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const gerarPGR = async () => {
    try {
      const r = await axios.post(`${API}/api/gridmind/campanhas/${campanha.id}/gerar-pgr`, {}, auth());
      toast.success(`PGR atualizado: ${r.data.riscos_inventariados} risco(s) psicossocial(is)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const criar = async () => {
    if (!form.titulo.trim()) { toast.error('Informe o título'); return; }
    try { await axios.post(`${API}/api/gridmind/planos`, { ...form, campanha_id: campanha.id }, auth()); toast.success('Plano criado'); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const mudarStatus = async (p, status) => {
    try { await axios.put(`${API}/api/gridmind/planos/${p.id}`, { status }, auth()); load(); }
    catch { toast.error('Erro'); }
  };
  const excluir = async (p) => {
    if (!window.confirm('Excluir plano?')) return;
    try { await axios.delete(`${API}/api/gridmind/planos/${p.id}`, auth()); load(); } catch { toast.error('Erro'); }
  };

  if (!campanha) return <SeletorVazio campanhas={campanhas} setSelecionada={setSelecionada} />;

  return (
    <div className="space-y-4">
      <SeletorCampanha campanhas={campanhas} campanha={campanha} setSelecionada={setSelecionada} />
      <div className="flex flex-wrap gap-2 justify-between">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={gerarAuto}><Sparkles className="w-4 h-4 mr-1" /> Gerar planos (riscos altos)</Button>
          <Button variant="outline" size="sm" onClick={gerarPGR}><ShieldCheck className="w-4 h-4 mr-1" /> Integrar ao PGR</Button>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-1" /> Novo plano</Button>
      </div>

      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" /></div>
        : planos.length === 0 ? <Card><CardContent className="py-10 text-center text-slate-400">Nenhum plano. Gere automaticamente ou crie manualmente.</CardContent></Card>
        : (
          <div className="grid gap-3">
            {planos.map(p => (
              <Card key={p.id}>
                <CardContent className="py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{p.titulo}</span>
                        {p.auto_gerado && <Badge className="bg-indigo-50 text-indigo-600 border border-indigo-200">Auto</Badge>}
                        {p.nivel_risco && <NivelBadge nivel={p.nivel_risco} />}
                      </div>
                      {p.descricao && <p className="text-sm text-slate-500 mt-1">{p.descricao}</p>}
                      {p.consequencia && <p className="text-xs text-slate-400 mt-1"><b>Consequência:</b> {p.consequencia}</p>}
                      <p className="text-xs text-slate-400 mt-1">
                        Responsável: {p.responsavel || '—'} · Prazo: {p.data_limite || '—'} · Evidências: {p.evidencias_count || 0}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <Select value={p.status} onValueChange={(s) => mudarStatus(p, s)}>
                        <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aberto">Aberto</SelectItem>
                          <SelectItem value="em_andamento">Em andamento</SelectItem>
                          <SelectItem value="concluido">Concluído</SelectItem>
                          <SelectItem value="cancelado">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setEvidOpen(p)}><Paperclip className="w-3.5 h-3.5 mr-1" /> Evidências</Button>
                        <Button size="sm" variant="ghost" onClick={() => excluir(p)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo Plano de Ação</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} /></div>
            <div><Label>Descrição</Label><Textarea value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Responsável</Label><Input value={form.responsavel} onChange={e => setForm({ ...form, responsavel: e.target.value })} /></div>
              <div><Label>Prazo</Label><Input type="date" value={form.data_limite} onChange={e => setForm({ ...form, data_limite: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={criar} className="bg-indigo-600 hover:bg-indigo-700">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {evidOpen && <EvidenciasDialog plano={evidOpen} onClose={() => { setEvidOpen(null); load(); }} />}
    </div>
  );
};

const EvidenciasDialog = ({ plano, onClose }) => {
  const [evid, setEvid] = useState([]);
  const [desc, setDesc] = useState('');
  const [tipo, setTipo] = useState('implementacao');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/gridmind/planos/${plano.id}/evidencias`, auth()); setEvid(r.data || []); } catch {}
  }, [plano.id]);
  useEffect(() => { load(); }, [load]);

  const adicionar = async () => {
    if (!desc.trim()) { toast.error('Descreva a evidência'); return; }
    setSaving(true);
    try {
      let arquivo_base64 = null, arquivo_nome = null;
      if (file) {
        arquivo_base64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
        arquivo_nome = file.name;
      }
      await axios.post(`${API}/api/gridmind/planos/${plano.id}/evidencias`, { descricao: desc, tipo, arquivo_base64, arquivo_nome }, auth());
      toast.success('Evidência registrada'); setDesc(''); setFile(null); load();
    } catch { toast.error('Erro'); } finally { setSaving(false); }
  };
  const remover = async (e) => { try { await axios.delete(`${API}/api/gridmind/evidencias/${e.id}`, auth()); load(); } catch {} };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Evidências — {plano.titulo}</DialogTitle><DialogDescription>Comprovação de implementação e reavaliação.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2 border rounded-md p-3">
            <Input placeholder="Descrição da evidência" value={desc} onChange={e => setDesc(e.target.value)} />
            <div className="flex gap-2">
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="implementacao">Implementação</SelectItem>
                  <SelectItem value="resultado">Resultado</SelectItem>
                  <SelectItem value="relatorio">Relatório</SelectItem>
                  <SelectItem value="reavaliacao">Reavaliação</SelectItem>
                </SelectContent>
              </Select>
              <Input type="file" onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>
            <Button size="sm" onClick={adicionar} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Adicionar
            </Button>
          </div>
          <div className="space-y-2 max-h-60 overflow-auto">
            {evid.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Sem evidências.</p>
              : evid.map(e => (
                <div key={e.id} className="flex items-center justify-between border rounded-md p-2 text-sm">
                  <div>
                    <p className="text-slate-700">{e.descricao}</p>
                    <p className="text-xs text-slate-400">{e.tipo}{e.arquivo_nome ? ` · ${e.arquivo_nome}` : ''}</p>
                  </div>
                  <div className="flex gap-1">
                    {e.arquivo_nome && <a href={`${API}/api/gridmind/evidencias/${e.id}/arquivo`} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs underline">baixar</a>}
                    <Button size="sm" variant="ghost" onClick={() => remover(e)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// Aba: Relatórios
// =========================================================================
const RelatoriosTab = ({ campanha, campanhas, setSelecionada }) => {
  if (!campanha) return <SeletorVazio campanhas={campanhas} setSelecionada={setSelecionada} />;
  const tipos = [
    { id: 'executivo', nome: 'Executivo', desc: 'Visão de gestão' },
    { id: 'sst', nome: 'Técnico SST', desc: 'Escalas, fatores e perguntas' },
    { id: 'nr1', nome: 'NR-1', desc: 'Riscos psicossociais e medidas' },
    { id: 'pgr', nome: 'PGR', desc: 'Inventário + plano de ação' },
    { id: 'auditoria', nome: 'Auditoria', desc: 'Parâmetros e rastreabilidade' },
  ];
  const baixar = (tipo, ext) => {
    const url = `${API}/api/gridmind/campanhas/${campanha.id}/relatorio/${tipo}.${ext}`;
    fetch(url, auth()).then(r => r.blob()).then(b => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(b);
      a.download = `gridmind_${tipo}_${campanha.codigo}.${ext}`; a.click();
    }).catch(() => toast.error('Erro ao gerar relatório'));
  };
  return (
    <div className="space-y-4">
      <SeletorCampanha campanhas={campanhas} campanha={campanha} setSelecionada={setSelecionada} />
      <div className="grid sm:grid-cols-2 gap-3">
        {tipos.map(t => (
          <Card key={t.id}>
            <CardContent className="py-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-800">{t.nome}</p>
                <p className="text-xs text-slate-500">{t.desc}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => baixar(t.id, 'pdf')}><FileText className="w-4 h-4 mr-1" /> PDF</Button>
                <Button size="sm" variant="outline" onClick={() => baixar(t.id, 'xlsx')}><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// =========================================================================
// Aba: Indicadores complementares
// =========================================================================
const IndicadoresTab = ({ campanha, campanhas, setSelecionada }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!campanha) return;
    setLoading(true);
    try { const r = await axios.get(`${API}/api/gridmind/campanhas/${campanha.id}/indicadores`, auth()); setData(r.data); }
    catch { toast.error('Erro'); } finally { setLoading(false); }
  }, [campanha]);
  useEffect(() => { load(); }, [load]);

  if (!campanha) return <SeletorVazio campanhas={campanhas} setSelecionada={setSelecionada} />;
  return (
    <div className="space-y-4">
      <SeletorCampanha campanhas={campanhas} campanha={campanha} setSelecionada={setSelecionada} />
      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" /></div> : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data?.indicadores || []).map((ind, i) => (
              <Card key={i}>
                <CardContent className="py-4">
                  <p className="text-sm text-slate-600">{ind.indicador}</p>
                  {ind.sem_dados ? (
                    <p className="text-lg font-semibold text-slate-300 mt-1">sem dados</p>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-slate-800 mt-1">{ind.valor ?? '—'}{ind.indicador.includes('turnover') ? '%' : ''}</p>
                      {ind.detalhe && <p className="text-xs text-slate-400">{ind.detalhe}</p>}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {data?.observacao && <p className="text-xs text-slate-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {data.observacao}</p>}
        </>
      )}
    </div>
  );
};

// =========================================================================
// Aba: Evidências (inventário no nível da campanha — NR-1/rastreabilidade)
// =========================================================================
const EvidenciasTab = ({ campanha, campanhas, setSelecionada }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!campanha) return;
    setLoading(true);
    try { const r = await axios.get(`${API}/api/gridmind/campanhas/${campanha.id}/evidencias`, auth()); setData(r.data); }
    catch { toast.error('Erro ao carregar evidências'); } finally { setLoading(false); }
  }, [campanha]);
  useEffect(() => { load(); }, [load]);

  if (!campanha) return <SeletorVazio campanhas={campanhas} setSelecionada={setSelecionada} />;

  const dt = (s) => s ? new Date(s).toLocaleString('pt-BR') : '—';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SeletorCampanha campanhas={campanhas} campanha={campanha} setSelecionada={setSelecionada} />
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Atualizar</Button>
      </div>
      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" /></div> : (
        <div className="space-y-4">
          {/* Snapshots de resultado / reavaliações */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Histórico de resultados / reavaliações</CardTitle>
              <CardDescription>Snapshots reais de cada cálculo registrado (sem dados estimados).</CardDescription></CardHeader>
            <CardContent>
              {(data?.snapshots_resultado || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nenhum cálculo registrado ainda.</p>
              ) : (
                <div className="space-y-2">
                  {(data.snapshots_resultado || []).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                      <span className="text-slate-500">{dt(s.data)}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-slate-600">{s.total_respondentes ?? 0} resp.</span>
                        <span className="text-slate-700">média {fmt(s.media_geral)}</span>
                        <NivelBadge nivel={s.nivel_geral} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Relatórios emitidos */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Relatórios emitidos</CardTitle>
              <CardDescription>Registro de cada relatório PDF/Excel emitido para a campanha.</CardDescription></CardHeader>
            <CardContent>
              {(data?.relatorios_emitidos || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nenhum relatório emitido ainda.</p>
              ) : (
                <div className="space-y-2">
                  {(data.relatorios_emitidos || []).map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                      <span className="text-slate-700">{r.descricao}</span>
                      <span className="text-slate-500">{dt(r.criado_em)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Evidências dos planos de ação */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Paperclip className="w-4 h-4" /> Evidências dos planos de ação</CardTitle>
              <CardDescription>Comprovações anexadas às ações de tratamento.</CardDescription></CardHeader>
            <CardContent>
              {(data?.evidencias_planos || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nenhuma evidência anexada aos planos.</p>
              ) : (
                <div className="space-y-2">
                  {(data.evidencias_planos || []).map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                      <span className="text-slate-700">
                        <Badge className="bg-slate-100 text-slate-600 mr-2">{e.tipo}</Badge>
                        {e.descricao}
                        {e.plano_titulo && <span className="text-slate-400"> · {e.plano_titulo}</span>}
                      </span>
                      <span className="text-slate-500">{dt(e.criado_em)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {(data?.outras_evidencias || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Outras evidências da campanha</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(data.outras_evidencias || []).map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                      <span className="text-slate-700"><Badge className="bg-slate-100 text-slate-600 mr-2">{e.tipo}</Badge>{e.descricao}</span>
                      <span className="text-slate-500">{dt(e.criado_em)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

const SeletorVazio = ({ campanhas, setSelecionada }) => (
  <Card><CardContent className="py-10 text-center text-slate-400 space-y-3">
    <p>Selecione uma campanha para continuar.</p>
    <div className="max-w-xs mx-auto">
      <Select onValueChange={(id) => setSelecionada(campanhas.find(c => c.id === id))}>
        <SelectTrigger><SelectValue placeholder="Selecionar campanha" /></SelectTrigger>
        <SelectContent>{campanhas.map(c => <SelectItem key={c.id} value={c.id}>{c.codigo}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  </CardContent></Card>
);

export default GridMindPage;
